/* ============================================================================
   Kiive Audio Compare engine v2.2
   ----------------------------------------------------------------------------
   What changed from v1:

   1. AUTO GEOMETRY. The engine reads each track video's real pixel size on
      loadedmetadata, treats half the height as one state, and sizes the
      container to that exact aspect. The video is mapped at 200% height with
      a 0% / -50% shift between Bypass and On, so the split is exact by
      construction at any export resolution. The section's "Stacked MP4
      width/height" fields are no longer used. Offsets (section and per-track
      CSS vars) still apply as an optional trim and default to 0.

   2. ONE WIDTH KNOB. The video container is capped by --display-max-desk
      (the "Visual width" setting) on desktop and --display-max-mobile on
      mobile. The legacy grid column is widened to max(gui width, visual
      width) so GUI max width can never silently cap the video, and the
      display box's typed aspect-ratio is neutralized. Visual width is the
      single authority; the typed Stacked MP4 fields are ignored.

   3. RE-EXECUTION SAFE + EDITOR AWARE. Everything lives in an IIFE with a
      window.KiiveAW guard, so loading this file twice just re-boots instead
      of throwing. The engine listens for shopify:section:load/unload (these
      only fire inside the theme editor) and re-initializes fresh DOM after
      every setting change, so the player no longer goes dark while editing.

   4. SHARED AUDIO CONTEXT. One lazy AudioContext serves every instance and
      every editor re-render, so long editing sessions can't exhaust the
      browser's context limit. Disconnected instances are destroyed and their
      resources released.

   5. RACE GUARDS. Rapid track switching can no longer start audio for a
      stale track; every async path checks an epoch token after awaiting.

   6. ANALYSER TAP (new in 2.2). Every buffer source now routes through one
      shared AnalyserNode before hitting the destination, so section-level
      visuals can read the real waveform and spectrum of whatever is playing.
      The tap is exposed as window.KiiveAW.peekAnalyser(), which returns null
      until the first user gesture creates the audio context, and the live
      analyser after that. It never creates a context on its own, so the
      lazy-load behavior from 2.1 (no fetch, no decode, no context at page
      load) is fully preserved. Audibly this changes nothing; the analyser
      is a pass-through.

   Layout rules are injected into <head> after all stylesheets with high
   specificity, so they win over kiive-audiowidget.css without editing it.
   ============================================================================ */

(function () {
  'use strict';

  /* Re-execution guard: a second load of this file just re-boots. */
  if (window.KiiveAW && typeof window.KiiveAW.boot === 'function') {
    window.KiiveAW.boot();
    return;
  }

  /* ── Injected layout (wins over the legacy stylesheet) ─────────────────── */

  var LAYOUT_STYLE_ID = 'kvaw-layout-styles';

  function injectLayoutStyles() {
    if (document.getElementById(LAYOUT_STYLE_ID)) return;
    var css = [
      /* Map the section-level offset vars to state trims, per breakpoint. */
      '.kiive-aw{--kvaw-off-processed:var(--processed-offset,0px);--kvaw-off-bypass:var(--bypass-offset,0px);}',
      '@media(max-width:768px){.kiive-aw{--kvaw-off-processed:var(--processed-offset-mobile,0px);--kvaw-off-bypass:var(--bypass-offset-mobile,0px);}}',

      /* The display wrapper just centers the container. aspect-ratio:auto kills
         the legacy stylesheet's typed-numbers box shape (the black letterbox
         source); the frame now wraps the measured container exactly. */
      '.kiive-aw .kiive-aw__display{height:auto!important;min-height:0!important;aspect-ratio:auto!important;display:flex!important;align-items:center!important;justify-content:center!important;width:100%!important;}',

      /* The legacy grid caps the media column at --gui-max-width, which could
         silently cap the video below Visual width. Let the column grow to
         whichever of the two is larger, so Visual width always wins. */
      '.kiive-aw .kiive-aw__grid{grid-template-columns:var(--tracklist-width,320px) minmax(0,max(var(--gui-max-width,760px),var(--display-max-desk,760px)))!important;}',
      '@media(min-width:769px){.kiive-aw .kiive-aw__videoControls{max-width:max(var(--gui-max-width,760px),var(--display-max-desk,760px))!important;}}',
      '@media(max-width:768px){.kiive-aw .kiive-aw__grid{grid-template-columns:1fr!important;}.kiive-aw .kiive-aw__videoControls{max-width:max(var(--gui-max-width-mobile,320px),var(--display-max-mobile,320px))!important;}}',

      /* Container: width capped by the Visual width setting, height derived
         from the measured single-state aspect ratio. */
      '.kiive-aw [data-kiive-aw-video-container]{position:relative!important;overflow:hidden!important;width:100%!important;max-width:var(--display-max-desk,760px)!important;height:auto!important;margin:0 auto!important;aspect-ratio:var(--kvaw-aspect,2.35/1)!important;background:transparent!important;}',
      '@media(max-width:768px){.kiive-aw [data-kiive-aw-video-container]{max-width:var(--display-max-mobile,320px)!important;}}',

      /* Video: exactly two stacked states. 200% height + fill = pixel-exact
         mapping, shifted by 0% (Bypass, top half) or -50% (On, bottom half),
         plus the optional trim var. */
      '.kiive-aw [data-kiive-aw-video-container] video{position:absolute!important;top:0!important;left:0!important;width:100%!important;height:200%!important;max-width:none!important;max-height:none!important;object-fit:fill!important;transform:translateY(var(--kvaw-shift,0%)) translateY(var(--kvaw-off,0px))!important;will-change:transform;}'
    ].join('\n');
    var el = document.createElement('style');
    el.id = LAYOUT_STYLE_ID;
    el.textContent = css;
    document.head.appendChild(el);
  }

  /* ── Shared audio context + analyser tap (lazy, reused by all) ─────────── */

  var sharedCtx = null;
  var sharedAnalyser = null;

  function getAudioContext() {
    if (sharedCtx && sharedCtx.state !== 'closed') return sharedCtx;
    var AC = window.AudioContext || window.webkitAudioContext;
    var mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    sharedCtx = mobile ? new AC({ latencyHint: 'playback', sampleRate: 44100 }) : new AC();
    return sharedCtx;
  }

  /* One analyser sits between every buffer source and the destination.
     Pure pass-through; exists so visuals can read real audio data. */
  function getAnalyser() {
    var ctx = getAudioContext();
    if (!sharedAnalyser || sharedAnalyser.context !== ctx) {
      try { if (sharedAnalyser) sharedAnalyser.disconnect(); } catch (_) {}
      sharedAnalyser = ctx.createAnalyser();
      sharedAnalyser.fftSize = 2048;
      sharedAnalyser.smoothingTimeConstant = 0.82;
      sharedAnalyser.connect(ctx.destination);
    }
    return sharedAnalyser;
  }

  /* Visuals call this every frame. Returns null before the first user
     gesture (no context yet) so it can never defeat lazy loading. */
  function peekAnalyser() {
    if (!sharedCtx || sharedCtx.state === 'closed') return null;
    return getAnalyser();
  }

  /* ── Widget ─────────────────────────────────────────────────────────────── */

  var KiiveAudioWidget = class {
    constructor(root) {
      this.root = root;

      this.video = root.querySelector('[data-kiive-aw-video]');
      this.videoContainer = root.querySelector('[data-kiive-aw-video-container]');
      this.playBtn = root.querySelector('[data-kiive-aw-play]');
      this.switchBtns = root.querySelectorAll('[data-kiive-aw-switch]');
      this.trackListEl = root.querySelector('[data-kiive-aw-tracklist]');
      this.titleEl = root.querySelector('[data-kiive-aw-title]');
      this.prevBtn = root.querySelector('[data-kiive-aw-prev]');
      this.nextBtn = root.querySelector('[data-kiive-aw-next]');
      this.loading = root.querySelector('[data-kiive-aw-loading]');
      this.progressBar = root.querySelector('[data-kiive-aw-progress]');
      this.sourceSelect = root.querySelector('[data-kiive-aw-source]');
      this.seekControl = root.querySelector('[data-kiive-aw-seek]');

      var dataEl = root.querySelector('[data-kiive-aw-data]');
      this.data = JSON.parse((dataEl && dataEl.textContent) || '[]');
      this.currentIndex = 0;
      this.consoleV2 = root.classList.contains('kiive-aw--console-v2');
      this.sticky = this.consoleV2 ? document.getElementById(root.getAttribute('data-v2-sticky-id')) : null;
      this.stickyPlayBtn = this.sticky && this.sticky.querySelector('[data-kiive-aw-sticky-play]');
      this.stickySource = this.sticky && this.sticky.querySelector('[data-kiive-aw-sticky-source]');
      this.stickySeek = this.sticky && this.sticky.querySelector('[data-kiive-aw-sticky-seek]');
      this.stickySwitchBtns = this.sticky ? this.sticky.querySelectorAll('[data-kiive-aw-sticky-switch]') : [];
      this.stickyProduct = this.sticky && this.sticky.querySelector('[data-kiive-aw-sticky-product]');
      this.stickyProductTitle = this.stickyProduct ? this.stickyProduct.textContent : '';

      this.state = 'bypass'; /* 'processed' or 'bypass' */
      this.isPlaying = false;

      /* Cache decoded buffers: key = `${index}:${state}` */
      this.bufferCache = new Map();
      /* V2 caches complete pairs, including at most two recently used pairs. */
      this.pairCache = new Map();
      this.pairLoads = new Map();
      this.pair = null;
      this.sources = null;
      this.gains = null;
      this.commonDuration = 0;
      this._playPromise = null;
      this._v2Error = false;
      this._v2Loading = false;
      this._scrubbingControl = null;
      this._v2Frame = null;
      this._stickyInteracted = false;
      this._stickyDismissed = false;
      this._mainAbove = false;
      this._stickyObserver = null;
      if (this.consoleV2) {
        this.baseOffsets = ['--processed-offset', '--bypass-offset', '--processed-offset-mobile', '--bypass-offset-mobile'].map(function (name) {
          return parseFloat(root.style.getPropertyValue(name)) || 0;
        });
        this._onAudioActive = this.onAudioActive.bind(this);
        document.addEventListener('kiive:audio-active', this._onAudioActive);
        this._onVideoTime = this.syncVideo.bind(this);
        if (this.video) this.video.addEventListener('timeupdate', this._onVideoTime);
      }

      /* Currently playing buffer source */
      this.currentSource = null;
      this.startTime = 0;
      this.pausedTime = 0;

      /* Bumped on track switch / destroy; async paths bail if it moved. */
      this._epoch = 0;

      this._onVis = this._onVisibilityChange.bind(this);
      this._onMeta = this.applyVideoLayout.bind(this);
      if (this.video) this.video.addEventListener('loadedmetadata', this._onMeta);

      this.bindUI();
      this.renderTrackList();
      if (this.consoleV2) this.renderSourceSelect();
      if (this.sticky) {
        this.bindStickyUI();
        this.observeStickyPosition();
        this.syncStickyControls();
        this.syncStickyVisibility();
      }

      /* Lazy audio (July 2026): no fetch, no decode, no AudioContext on page
         load. warm:false still paints the title and pulls the video's tiny
         metadata, but the first audio buffer now downloads on first play
         (the existing loading bar covers the wait) or a beat earlier via the
         intent warm below. Previously every player instance pulled a full
         audio file and decoded it before the visitor did anything, which on
         a two-player PDP meant two multi-megabyte downloads at page load. */
      this.loadTrack(this.currentIndex, { warm: false }).catch(console.error);

      /* One-shot intent warm: the first hover / touch / focus anywhere on
         the widget starts the download early, so pressing play feels
         instant for visitors who are clearly about to use it. Guarded so
         overlapping intent events can't trigger a double fetch. */
      var warmSelf = this;
      var warmed = false;
      var warmOnce = function () {
        if (warmed) return;
        warmed = true;
        if (warmSelf.consoleV2) {
          warmSelf.getPair(warmSelf.currentIndex).catch(function (e) { warmSelf.reportV2Error(e); });
        } else {
          warmSelf.getBuffer(warmSelf.currentIndex, warmSelf.state).catch(function () {});
        }
      };
      this.root.addEventListener('pointerenter', warmOnce, { once: true });
      this.root.addEventListener('touchstart', warmOnce, { once: true, passive: true });
      this.root.addEventListener('focusin', warmOnce, { once: true });
    }

    /* ── Geometry ─────────────────────────────────────────────────────── */

    applyVideoLayout() {
      if (!this.video || !this.videoContainer) return;
      var vw = this.video.videoWidth;
      var vh = this.video.videoHeight;
      if (!vw || !vh) return;
      /* Single-product videos use their full frame; stacked exports retain
         the existing half-height geometry and On/Bypass reveal. */
      var single = this.consoleV2 && this.root.getAttribute('data-v2-media') === 'single';
      this.videoContainer.style.setProperty('--kvaw-aspect', vw + ' / ' + (single ? vh : vh / 2));
      this.applyStateShift();
      if (this.consoleV2) this.alignVideo(this.currentPlayhead);
    }

    applyStateShift() {
      if (!this.videoContainer) return;
      if (this.consoleV2 && this.root.getAttribute('data-v2-media') === 'single') return;
      var processed = this.state === 'processed';
      this.videoContainer.style.setProperty('--kvaw-shift', processed ? '-50%' : '0%');
      this.videoContainer.style.setProperty('--kvaw-off', processed ? 'var(--kvaw-off-processed)' : 'var(--kvaw-off-bypass)');
      /* Legacy hook kept for the old stylesheet: added = show bottom (On). */
      this.videoContainer.classList.toggle('is-bypassed', processed);
    }

    /* ── Misc helpers ─────────────────────────────────────────────────── */

    _onVisibilityChange() {
      if (document.hidden && this.isPlaying) this.stop();
    }

    reportV2Error(error) {
      console.error('Kiive Console V2: track ' + this.currentIndex + ' could not load or decode its On/Bypass audio pair', error);
      this._v2Error = true;
      this.root.classList.add('has-error');
      if (this.loading) this.loading.textContent = 'Could not load audio. Choose another source.';
      if (this.playBtn) this.playBtn.disabled = false;
      this.syncStickyControls();
    }

    onAudioActive(event) {
      if (event.detail && event.detail.source !== this.root) this.stop();
    }

    emitAudioActive() {
      document.dispatchEvent(new CustomEvent('kiive:audio-active', { detail: { source: this.root } }));
    }

    async ensureAudioContext() {
      var ctx = getAudioContext();
      if (ctx.state === 'suspended') await ctx.resume();
      return ctx;
    }

    showLoading(on) {
      if (!this.loading) return;
      if (this.consoleV2) {
        this._v2Loading = on;
        if (on) {
          this._v2Error = false;
          this.root.classList.remove('has-error');
        }
        this.root.classList.toggle('is-loading', on);
        this.loading.textContent = on ? 'Loading audio…' : (this._v2Error ? 'Could not load audio. Choose another source.' : '');
        if (this.playBtn) this.playBtn.disabled = on && !this.isPlaying;
        this.syncStickyControls();
        return;
      }
      this.loading.style.display = on ? 'flex' : 'none';
      if (this.progressBar) this.progressBar.style.width = on ? '15%' : '0%';
    }

    setProgress(pct) {
      if (this.progressBar) this.progressBar.style.width = Math.max(0, Math.min(100, pct)) + '%';
    }

    escapeHTML(str) {
      return String(str).replace(/[&<>"']/g, function (m) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m];
      });
    }

    /* ── UI wiring ────────────────────────────────────────────────────── */

    bindUI() {
      var self = this;

      if (this.playBtn) {
        this.playBtn.addEventListener('click', async function () {
          if (self.consoleV2) self.noteMainInteraction();
          if (self.isPlaying) {
            self.stop();
          } else {
            try { await self.play(); } catch (e) { if (self.consoleV2) self.reportV2Error(e); else console.error(e); }
          }
        });
      }

      this.switchBtns.forEach(function (btn) {
        btn.addEventListener('click', async function () {
          var nextState = btn.getAttribute('data-kiive-aw-switch');
          if (!nextState) return;

          if (self.consoleV2) {
            self.noteMainInteraction();
            self.setV2CompareState(nextState);
            return;
          }

          self.switchBtns.forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');

          self.state = nextState;

          /* Video reveal: geometry-driven, exact halves plus optional trim. */
          self.applyStateShift();

          /* Audio A/B (keeps playhead position) */
          try { await self.switchAudioState(self.state); } catch (e) { if (self.consoleV2) self.reportV2Error(e); else console.error(e); }
        });
      });

      /* Track click (desktop list) */
      this.root.addEventListener('click', async function (e) {
        var btn = e.target.closest('[data-kiive-aw-track]');
        if (!btn) return;
        var idx = Number(btn.getAttribute('data-kiive-aw-track'));
        if (Number.isNaN(idx)) return;
        await self.switchTrack(idx, { autoplay: true });
      });

      /* Prev/next */
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', async function () {
          var idx = (self.currentIndex - 1 + self.data.length) % self.data.length;
          await self.switchTrack(idx, { autoplay: true });
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', async function () {
          var idx = (self.currentIndex + 1) % self.data.length;
          await self.switchTrack(idx, { autoplay: true });
        });
      }

      if (this.consoleV2 && this.sourceSelect) {
        this.sourceSelect.addEventListener('change', function () {
          self.noteMainInteraction();
          var index = Number(self.sourceSelect.value);
          if (Number.isInteger(index)) self.switchTrack(index, { autoplay: true });
        });
      }
      if (this.consoleV2 && this.seekControl) {
        this.seekControl.addEventListener('input', function () {
          self.noteMainInteraction();
          self._scrubbingControl = self.seekControl;
          self.paintV2Seek(Number(self.seekControl.value), self.seekControl);
        });
        this.seekControl.addEventListener('change', function () {
          self.noteMainInteraction();
          var position = Number(self.seekControl.value);
          self._scrubbingControl = null;
          self.seek(position);
          self.updateV2Progress();
        });
      }

      document.addEventListener('visibilitychange', this._onVis);
    }

    noteMainInteraction() {
      if (!this.sticky) return;
      if (this._stickyDismissed) {
        var rect = this.root.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        this._stickyDismissed = false;
      }
      this._stickyInteracted = true;
      this.syncStickyVisibility();
    }

    observeStickyPosition() {
      if (!window.IntersectionObserver) return;
      var self = this;
      this._stickyObserver = new window.IntersectionObserver(function (entries) {
        var entry = entries[0];
        if (!entry) return;
        self._mainAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        self.syncStickyVisibility();
      }, { threshold: 0 });
      this._stickyObserver.observe(this.root);
    }

    syncStickyVisibility() {
      if (!this.sticky) return;
      var visible = this._stickyInteracted && this._mainAbove && !this._stickyDismissed;
      this.sticky.classList.toggle('is-visible', visible);
      this.sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      this.sticky.inert = !visible;
    }

    async setV2CompareState(nextState) {
      if (nextState !== 'processed' && nextState !== 'bypass') return;
      this.state = nextState;
      this.switchBtns.forEach(function (btn) {
        var active = btn.getAttribute('data-kiive-aw-switch') === nextState;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.applyStateShift();
      this.syncStickyControls();
      try { await this.switchAudioState(nextState); } catch (e) { this.reportV2Error(e); }
    }

    bindStickyUI() {
      var self = this;
      this.stickyPlayBtn.addEventListener('click', async function () {
        if (self.isPlaying) self.stop();
        else try { await self.play(); } catch (e) { self.reportV2Error(e); }
      });
      this.stickySource.addEventListener('change', function () {
        var index = Number(self.stickySource.value);
        if (Number.isInteger(index)) self.switchTrack(index, { autoplay: true });
      });
      this.stickySeek.addEventListener('input', function () {
        self._scrubbingControl = self.stickySeek;
        self.paintV2Seek(Number(self.stickySeek.value), self.stickySeek);
      });
      this.stickySeek.addEventListener('change', function () {
        var position = Number(self.stickySeek.value);
        self._scrubbingControl = null;
        self.seek(position);
        self.updateV2Progress();
      });
      this.stickySwitchBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.setV2CompareState(btn.getAttribute('data-kiive-aw-sticky-switch'));
        });
      });
      this.sticky.querySelector('[data-kiive-aw-sticky-close]').addEventListener('click', function () {
        self._stickyDismissed = true;
        self.syncStickyVisibility();
      });
    }

    syncStickyControls() {
      if (!this.sticky) return;
      this.sticky.classList.toggle('is-loading', this._v2Loading);
      this.sticky.classList.toggle('has-error', this._v2Error);
      this.stickyPlayBtn.classList.toggle('is-playing', this.isPlaying);
      this.stickyPlayBtn.setAttribute('aria-label', this.isPlaying ? 'Pause audio' : 'Play audio');
      this.stickyPlayBtn.disabled = this._v2Loading && !this.isPlaying;
      this.stickySource.value = String(this.currentIndex);
      this.stickyProduct.textContent = this._v2Loading ? 'Loading audio…' :
        (this._v2Error ? 'Audio unavailable' : this.stickyProductTitle);
      this.stickySwitchBtns.forEach(function (btn) {
        var active = btn.getAttribute('data-kiive-aw-sticky-switch') === this.state;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      }, this);
    }

    renderSourceSelect() {
      if (!this.sourceSelect) return;
      var markup = '';
      var openGroup = null;
      for (var i = 0; i < this.data.length; i++) {
        var track = this.data[i];
        var group = String(track.group || '').trim();
        if (group !== openGroup) {
          if (openGroup) markup += '</optgroup>';
          if (group) markup += '<optgroup label="' + this.escapeHTML(group) + '">';
          openGroup = group;
        }
        markup += '<option value="' + i + '">' + this.escapeHTML(track.title || ('Track ' + (i + 1))) + '</option>';
      }
      if (openGroup) markup += '</optgroup>';
      this.sourceSelect.innerHTML = markup;
      this.sourceSelect.value = String(this.currentIndex);
      if (this.stickySource) {
        this.stickySource.innerHTML = markup;
        this.stickySource.value = String(this.currentIndex);
      }
    }

    renderTrackList() {
      if (!this.trackListEl) return;
      var self = this;
      this.trackListEl.innerHTML = this.data.map(function (t, i) {
        return '' +
          '<div class="kiive-aw__track">' +
            '<button type="button" data-kiive-aw-track="' + i + '" class="' + (i === self.currentIndex ? 'is-active' : '') + '">' +
              self.escapeHTML(t.title || ('Track ' + (i + 1))) +
            '</button>' +
          '</div>';
      }).join('');
    }

    setActiveTrackButton() {
      if (!this.trackListEl) return;
      var self = this;
      this.trackListEl.querySelectorAll('button[data-kiive-aw-track]').forEach(function (b) {
        var idx = Number(b.getAttribute('data-kiive-aw-track'));
        b.classList.toggle('is-active', idx === self.currentIndex);
      });
    }

    /* ── Audio loading ────────────────────────────────────────────────── */

    async fetchAndDecode(url) {
      var res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error('HTTP ' + res.status + ' for ' + url);
      var buf = await res.arrayBuffer();
      return await getAudioContext().decodeAudioData(buf);
    }

    cacheKey(index, state) {
      return index + ':' + state;
    }

    /* Lazy-load buffer for a track+state */
    async getBuffer(index, state) {
      var key = this.cacheKey(index, state);
      if (this.bufferCache.has(key)) return this.bufferCache.get(key);

      var track = this.data[index];
      var url = state === 'processed' ? track.on : track.off;
      if (!url) throw new Error('Missing audio url for ' + state + ' on track ' + index);

      this.setProgress(35);
      var decoded = await this.fetchAndDecode(url);
      this.setProgress(70);

      this.bufferCache.set(key, decoded);
      return decoded;
    }

    createSource(buffer) {
      var ctx = getAudioContext();
      var src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      /* Route through the shared analyser tap (2.2). The analyser is
         connected to ctx.destination, so this is audibly identical to
         connecting straight to the destination. */
      src.connect(getAnalyser());
      return src;
    }

    /* Console V2: a pair is the unit of lazy loading and eviction. In-flight
       requests are shared, and a stale completion cannot grow the cache. */
    async getPair(index) {
      if (this.pairCache.has(index)) {
        var cached = this.pairCache.get(index);
        this.pairCache.delete(index);
        this.pairCache.set(index, cached);
        return cached;
      }
      if (this.pairLoads.has(index)) return this.pairLoads.get(index);
      var track = this.data[index];
      if (!track || !track.on || !track.off) throw new Error('Missing On or Bypass URL on track ' + index);
      var self = this;
      var pending = Promise.all([this.fetchAndDecode(track.on), this.fetchAndDecode(track.off)]).then(function (buffers) {
        var duration = Math.min(buffers[0].duration, buffers[1].duration);
        if (!Number.isFinite(duration) || duration <= 0) throw new Error('Invalid decoded duration on track ' + index);
        if (Math.abs(buffers[0].duration - buffers[1].duration) > 0.05) {
          console.warn('Kiive Console V2: On/Bypass durations differ on track ' + index, buffers[0].duration, buffers[1].duration);
        }
        var pair = { on: buffers[0], off: buffers[1], duration: duration };
        if (self.root.isConnected) {
          self.pairCache.set(index, pair);
          self.prunePairs();
        }
        return pair;
      }).finally(function () { self.pairLoads.delete(index); });
      this.pairLoads.set(index, pending);
      return pending;
    }

    prunePairs() {
      while (this.pairCache.size > 3) {
        var oldest = this.pairCache.keys().next().value;
        if (oldest === this.currentIndex) {
          var current = this.pairCache.get(oldest);
          this.pairCache.delete(oldest);
          this.pairCache.set(oldest, current);
          continue;
        }
        this.pairCache.delete(oldest);
      }
    }

    get currentPlayhead() {
      if (!this.consoleV2 || !this.commonDuration) return this.pausedTime || 0;
      if (!this.isPlaying) return this.pausedTime;
      var elapsed = Math.max(0, getAudioContext().currentTime - this.startTime);
      return elapsed % this.commonDuration;
    }

    get totalDuration() {
      return this.consoleV2 ? this.commonDuration : 0;
    }

    paintV2Seek(position, control) {
      control = control || this.seekControl;
      if (!control) return;
      var duration = this.commonDuration || 0;
      var percent = duration ? Math.max(0, Math.min(100, position / duration * 100)) : 0;
      if (control.parentElement) control.parentElement.style.setProperty('--kvaw-v2-progress', percent + '%');
      control.setAttribute('aria-valuetext', Math.floor(position) + ' of ' + Math.floor(duration) + ' seconds');
    }

    updateV2Progress() {
      if (!this.seekControl) return;
      var duration = this.commonDuration || 0;
      var position = duration ? this.currentPlayhead : 0;
      [this.seekControl, this.stickySeek].forEach(function (control) {
        if (!control) return;
        control.disabled = !duration;
        control.max = duration || 1;
        if (this._scrubbingControl === control) return;
        control.value = position;
        this.paintV2Seek(position, control);
      }, this);
    }

    startV2Progress() {
      if (!this.seekControl || this._v2Frame !== null) return;
      var self = this;
      function frame() {
        self.updateV2Progress();
        self._v2Frame = self.isPlaying ? requestAnimationFrame(frame) : null;
      }
      this._v2Frame = requestAnimationFrame(frame);
    }

    stopV2Progress() {
      if (this._v2Frame !== null) cancelAnimationFrame(this._v2Frame);
      this._v2Frame = null;
      this.updateV2Progress();
    }

    /* Public engine hook for the Pass 2 progress control. Seek intentionally
       replaces the pair; only A/B switching is source-preserving. */
    seek(position) {
      if (!this.consoleV2 || !this.commonDuration || !Number.isFinite(position)) return false;
      var offset = Math.max(0, Math.min(position, this.commonDuration));
      if (offset === this.commonDuration) offset = 0;
      var wasPlaying = this.isPlaying;
      if (wasPlaying) this.releaseV2Sources();
      this.pausedTime = offset;
      if (wasPlaying) this.startV2Sources(this.pair, offset);
      this.alignVideo(offset);
      if (wasPlaying && this.video) this.video.play().catch(function () {});
      this.updateV2Progress();
      return true;
    }

    releaseV2Sources() {
      if (!this.sources) return;
      this.sources.forEach(function (source) {
        try { source.stop(); } catch (_) {}
        try { source.disconnect(); } catch (_) {}
      });
      this.gains.forEach(function (gain) { try { gain.disconnect(); } catch (_) {} });
      this.sources = null;
      this.gains = null;
      this._fade = null;
    }

    startV2Sources(pair, offset) {
      var ctx = getAudioContext();
      var when = ctx.currentTime + 0.015;
      var onGain = ctx.createGain();
      var offGain = ctx.createGain();
      onGain.gain.value = this.state === 'processed' ? 1 : 0;
      offGain.gain.value = this.state === 'bypass' ? 1 : 0;
      onGain.connect(getAnalyser());
      offGain.connect(getAnalyser());
      var on = ctx.createBufferSource();
      var off = ctx.createBufferSource();
      on.buffer = pair.on;
      off.buffer = pair.off;
      [on, off].forEach(function (source, i) {
        source.loop = true;
        source.loopStart = 0;
        source.loopEnd = pair.duration;
        source.connect(i === 0 ? onGain : offGain);
      });
      this.gains = [onGain, offGain];
      this.sources = [on, off];
      this.startTime = when - offset;
      this.pausedTime = offset;
      try {
        on.start(when, offset);
        off.start(when, offset);
      } catch (e) {
        this.releaseV2Sources();
        throw e;
      }
      this.isPlaying = true;
      if (this.playBtn) {
        this.playBtn.classList.add('is-playing');
        this.playBtn.setAttribute('aria-label', 'Pause audio');
        this.playBtn.disabled = false;
      }
      this.syncStickyControls();
      var wave = this.root.querySelector('[data-kvaw-wave]');
      if (wave) wave.classList.add('is-playing');
      this.updateV2Progress();
      this.startV2Progress();
    }

    fadeV2(nextState) {
      if (!this.isPlaying || !this.gains) return;
      var now = getAudioContext().currentTime;
      var targets = nextState === 'processed' ? [1, 0] : [0, 1];
      var previous = this._fade;
      var from = this.gains.map(function (gain, i) {
        if (!previous) return gain.gain.value;
        var fraction = Math.max(0, Math.min(1, (now - previous.at) / 0.025));
        return previous.from[i] + (previous.to[i] - previous.from[i]) * fraction;
      });
      this.gains.forEach(function (gain, i) {
        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(from[i], now);
        gain.gain.linearRampToValueAtTime(targets[i], now + 0.025);
      });
      this._fade = { at: now, from: from, to: targets };
    }

    alignVideo(position) {
      if (!this.video || this.video.readyState < 1) return;
      var duration = this.video.duration;
      var target = Number.isFinite(duration) && duration > 0 ? position % duration : position;
      try { this.video.currentTime = target; } catch (_) {}
    }

    syncVideo() {
      if (!this.isPlaying || !this.video || !this.commonDuration || !Number.isFinite(this.video.duration) || this.video.duration <= 0) return;
      var target = this.currentPlayhead % this.video.duration;
      var diff = Math.abs(this.video.currentTime - target);
      /* timeupdate is a media event, not a polling loop. Correct drift and
         the boundary when the MP4 is longer than the common audio pair. */
      if (Math.min(diff, Math.abs(diff - this.video.duration)) > 0.15) this.alignVideo(target);
    }

    playV2() {
      if (this.isPlaying) return Promise.resolve();
      if (this._playPromise && this._playEpoch === this._epoch) return this._playPromise;
      var self = this;
      var ep = this._epoch;
      this._playEpoch = ep;
      var index = this.currentIndex;
      this.showLoading(true);
      this._playPromise = (async function () {
        await self.ensureAudioContext();
        var pair = await self.getPair(index);
        if (ep !== self._epoch || !self.root.isConnected) return;
        self.pair = pair;
        self.commonDuration = pair.duration;
        self.updateV2Progress();
        var offset = self.pausedTime % pair.duration;
        self.emitAudioActive();
        if (ep !== self._epoch) return;
        self.startV2Sources(pair, offset);
        self.showLoading(false);
        self.alignVideo(offset);
        if (self.video) {
          try {
            await self.video.play();
            if (ep === self._epoch && self.isPlaying) self.alignVideo(self.currentPlayhead);
            else self.video.pause();
          } catch (_) { /* Audio-only remains usable if autoplay of MP4 fails. */ }
        }
      })().finally(function () {
        if (ep === self._epoch) self.showLoading(false);
        if (self._playEpoch === ep) self._playPromise = null;
      });
      return this._playPromise;
    }

    stopV2() {
      if (this.isPlaying) this.pausedTime = this.currentPlayhead;
      this._epoch++;
      this.releaseV2Sources();
      this.isPlaying = false;
      if (this.video) this.video.pause();
      if (this.playBtn) {
        this.playBtn.classList.remove('is-playing');
        this.playBtn.setAttribute('aria-label', 'Play audio');
      }
      var wave = this.root.querySelector('[data-kvaw-wave]');
      if (wave) wave.classList.remove('is-playing');
      this.stopV2Progress();
      this.showLoading(false);
    }

    /* ── Track loading / switching ────────────────────────────────────── */

    async loadTrack(index, opts) {
      opts = opts || {};
      var warm = !!opts.warm;
      var track = this.data[index];
      if (!track) return;

      if (this.titleEl) this.titleEl.textContent = track.title || '';

      if (this.consoleV2) {
        /* Legacy's section-inline offset poll is excluded for V2. */
        var names = ['--processed-offset', '--bypass-offset', '--processed-offset-mobile', '--bypass-offset-mobile'];
        var keys = ['processedOffset', 'bypassOffset', 'processedOffsetMobile', 'bypassOffsetMobile'];
        for (var i = 0; i < names.length; i++) {
          this.root.style.setProperty(names[i], (this.baseOffsets[i] + (parseFloat(track[keys[i]]) || 0)) + 'px');
        }
      }

      if (this.video) {
        var mediaSrc = this.consoleV2 && this.root.getAttribute('data-v2-media') === 'single'
          ? this.root.getAttribute('data-v2-single-video') : track.video;
        if (this.consoleV2) this.root.classList.toggle('is-video-missing', !mediaSrc);
        if (this.video.getAttribute('src') !== mediaSrc) {
          this.video.pause();
          this.video.setAttribute('src', mediaSrc || '');
          this.video.currentTime = this.consoleV2 ? 0 : 0.001;
          this.video.muted = true;
          this.video.loop = true;
          this.video.playsInline = true;
          /* Metadata is tiny and carries the dimensions the layout needs. */
          this.video.preload = 'metadata';
          this.video.load();
        }
        /* Sync geometry and state now if metadata is already in, otherwise
           the loadedmetadata listener handles it. */
        if (this.video.readyState >= 1) {
          this.applyVideoLayout();
        } else {
          this.applyStateShift();
        }
      }

      if (warm) {
        var ep = this._epoch;
        try {
          this.showLoading(true);
          if (this.consoleV2) {
            var loadedPair = await this.getPair(index);
            if (ep === this._epoch) {
              this.pair = loadedPair;
              this.commonDuration = loadedPair.duration;
              this.updateV2Progress();
            }
          } else await this.getBuffer(index, this.state);
        } finally {
          if (ep === this._epoch) {
            this.setProgress(100);
            if (this.consoleV2) this.showLoading(false);
            else {
              var self = this;
              setTimeout(function () { self.showLoading(false); }, 120);
            }
          }
        }
      }
    }

    async switchTrack(index, opts) {
      opts = opts || {};
      if (index === this.currentIndex) return;
      if (!this.data[index]) return;

      if (this.isPlaying || (this.consoleV2 && this._playPromise)) this.stop();

      this._epoch++;
      var ep = this._epoch;

      this.showLoading(true);
      this.setProgress(10);

      this.currentIndex = index;
      this.setActiveTrackButton();
      if (this.consoleV2 && this.sourceSelect) this.sourceSelect.value = String(index);
      if (this.consoleV2) {
        this._scrubbingControl = null;
        this.syncStickyControls();
      }

      this.startTime = 0;
      this.pausedTime = 0;
      if (this.consoleV2) {
        this.pair = null;
        this.commonDuration = 0;
        this.updateV2Progress();
      }

      try {
        await this.loadTrack(index, { warm: true });
        if (ep !== this._epoch) return;
        this.setProgress(85);
      } catch (e) {
        if (this.consoleV2) this.reportV2Error(e);
        else console.error(e);
        if (this.consoleV2) return;
      } finally {
        if (ep === this._epoch) {
          this.setProgress(100);
          if (this.consoleV2) this.showLoading(false);
          else {
            var self = this;
            setTimeout(function () { self.showLoading(false); }, 150);
          }
        }
      }

      if (opts.autoplay) {
        await new Promise(function (r) { setTimeout(r, 200); });
        if (ep !== this._epoch) return;
        try { await this.play(); } catch (e) { if (this.consoleV2) this.reportV2Error(e); else console.error(e); }
      }
    }

    /* ── Transport ────────────────────────────────────────────────────── */

    async play() {
      if (this.consoleV2) return this.playV2();
      await this.ensureAudioContext();

      var index = this.currentIndex;
      var ep = this._epoch;

      this.showLoading(true);
      this.setProgress(20);

      var buffer;
      try {
        buffer = await this.getBuffer(index, this.state);
        this.setProgress(70);
      } finally {
        this.setProgress(100);
        var self = this;
        setTimeout(function () { self.showLoading(false); }, 120);
      }

      /* A track switch or teardown happened while decoding: bail. */
      if (ep !== this._epoch || !this.root.isConnected) return;

      if (this.video) {
        this.video.currentTime = 0.001;
        try { await this.video.play(); } catch (_) {}
      }

      if (ep !== this._epoch) return;

      try {
        if (this.currentSource) {
          this.currentSource.stop();
          this.currentSource.disconnect();
        }
      } catch (_) {}

      this.currentSource = this.createSource(buffer);

      this.startTime = getAudioContext().currentTime - (this.pausedTime || 0);
      this.currentSource.start(0, (this.pausedTime || 0) % buffer.duration);

      this.isPlaying = true;
      if (this.playBtn) this.playBtn.classList.add('is-playing');

      /* Prefetch the other state in the background for instant A/B */
      var other = this.state === 'processed' ? 'bypass' : 'processed';
      this.getBuffer(index, other).catch(function () {});
    }

    stop() {
      if (this.consoleV2) return this.stopV2();
      try {
        if (this.currentSource) {
          this.currentSource.stop();
          this.currentSource.disconnect();
        }
      } catch (_) {}

      this.currentSource = null;

      if (this.isPlaying) {
        this.pausedTime = Math.max(0, getAudioContext().currentTime - this.startTime);
      } else {
        this.pausedTime = 0;
      }

      if (this.video) {
        var v = this.video;
        v.currentTime = 0.001;
        requestAnimationFrame(function () { v.pause(); });
      }

      this.isPlaying = false;
      if (this.playBtn) this.playBtn.classList.remove('is-playing');
    }

    async switchAudioState(nextState) {
      if (this.consoleV2) {
        this.fadeV2(nextState);
        return;
      }
      if (!this.data[this.currentIndex]) return;

      /* If not playing, just warm the buffer so first play is fast */
      if (!this.isPlaying) {
        this.getBuffer(this.currentIndex, nextState).catch(function () {});
        return;
      }

      await this.ensureAudioContext();

      var ep = this._epoch;
      var currentTime = getAudioContext().currentTime - this.startTime;
      var buffer = await this.getBuffer(this.currentIndex, nextState);

      if (ep !== this._epoch || !this.isPlaying) return;

      /* Swap buffer sources at the same playhead position */
      try {
        if (this.currentSource) {
          this.currentSource.stop();
          this.currentSource.disconnect();
        }
      } catch (_) {}

      this.currentSource = this.createSource(buffer);
      this.currentSource.start(0, currentTime % buffer.duration);
      this.startTime = getAudioContext().currentTime - currentTime;
    }

    /* ── Teardown (editor re-renders) ─────────────────────────────────── */

    destroy() {
      this._epoch++;
      if (this.consoleV2) {
        this.stopV2();
        if (this._stickyObserver) this._stickyObserver.disconnect();
        if (this.sticky) this.sticky.remove();
        document.removeEventListener('kiive:audio-active', this._onAudioActive);
        if (this.video) this.video.removeEventListener('timeupdate', this._onVideoTime);
        this.pairCache.clear();
        this.pairLoads.clear();
      }
      try {
        if (this.currentSource) {
          this.currentSource.stop();
          this.currentSource.disconnect();
        }
      } catch (_) {}
      this.currentSource = null;
      this.isPlaying = false;

      if (this.video) {
        try {
          this.video.removeEventListener('loadedmetadata', this._onMeta);
          this.video.pause();
        } catch (_) {}
      }

      document.removeEventListener('visibilitychange', this._onVis);
      this.bufferCache.clear();
      if (this.root.__kvaw === this) delete this.root.__kvaw;
    }
  };

  /* ── Boot / registry / editor lifecycle ─────────────────────────────── */

  var instances = new Set();

  function reap() {
    instances.forEach(function (w) {
      if (!w.root.isConnected) {
        try { w.destroy(); } catch (_) {}
        instances.delete(w);
      }
    });
  }

  function boot(scope) {
    injectLayoutStyles();
    var host = (scope && scope.querySelectorAll) ? scope : document;
    host.querySelectorAll('[data-kiive-audiowidget]').forEach(function (root) {
      if (root.__kvaw) return;
      try {
        root.__kvaw = new KiiveAudioWidget(root);
        instances.add(root.__kvaw);
      } catch (e) {
        console.error(e);
      }
    });
    reap();
  }

  window.KiiveAW = { boot: boot, instances: instances, peekAnalyser: peekAnalyser, version: '2.2' };

  /* Theme editor only: these events never fire on the live storefront.
     A setting change re-renders the section's HTML; boot the fresh DOM and
     release instances whose DOM was thrown away. */
  document.addEventListener('shopify:section:load', function (e) {
    reap();
    boot(e.target || document);
  });
  document.addEventListener('shopify:section:unload', function () {
    setTimeout(reap, 50);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
})();
